import React, {
    useState,
    type ComponentPropsWithRef,
    useRef,
    useEffect
} from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import toast, { toastConfig } from 'react-simple-toasts';
import 'react-simple-toasts/dist/theme/dark.css';
import { inputStyles } from './helpers';
import { clsxMerge } from '../../utils/classNameUtils';
import { ChevronDownIcon } from '../icons/ChevronDownIcon';
import useOnClickOutside from '../../customhooks/useOnClickOutside';
import { AlertCircleIcon } from '../icons/AlertCircleIcon';
import { HelpCircleIcon } from '../icons/HelpCircleIcon';
import { CopyOutlineIcon } from '../icons/CopyOutlineIcon';

type InputElementProps = ComponentPropsWithRef<'input'>;

toastConfig({ theme: 'dark' });
export interface InputProps extends InputElementProps {
    label?: string;
    tagValues?: string[];
    onChangeTagValues?: (vals: string[]) => void;
    infoText?: string;
    isError?: boolean;
    errorMessage?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    dropdownValue?: string;
    setDropdownValue?: (val: string) => void;
    dropdownOptions?: { label: string; value: string }[];
    variant?:
        | 'default'
        | 'website'
        | 'copy'
        | 'tags'
        | 'dropdownLeft'
        | 'dropdownRight';
}

const Input = ({
    label,
    tagValues = [],
    onChange,
    onChangeTagValues,
    infoText,
    className,
    size,
    rightIcon,
    leftIcon,
    isError,
    errorMessage,
    disabled,
    dropdownValue,
    dropdownOptions = [],
    setDropdownValue,
    variant = 'default',
    ...props
}: InputProps) => {
    const ref = useRef(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [value, setValue] = useState<string | number | undefined>(
        props.value as string
    );

    const [openDropdown, setOpenDropdown] = useState(false);

    useOnClickOutside(ref, () => setOpenDropdown(false));

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            if (value !== '') {
                onChangeTagValues?.([...tagValues, value as string]);
                setValue('');
            }
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        onChangeTagValues?.(tagValues.filter((tag) => tag !== tagToRemove));
    };

    useEffect(() => {
        setValue(value);
    }, [value]);

    const component = {
        default: (
            <div className='relative'>
                <div className='absolute inset-y-0 start-0 flex items-center px-3.5 pointer-events-none'>
                    {Boolean(leftIcon) && leftIcon}
                </div>
                <input
                    className={clsxMerge(
                        inputStyles({
                            variant: disabled
                                ? 'disabled'
                                : isError
                                ? 'error'
                                : 'default',
                            spacing: leftIcon ? 'left' : 'right'
                        }),
                        className
                    )}
                    onChange={(e) => {
                        setValue(e.target.value);
                        onChange?.(e);
                    }}
                    onBlur={props.onBlur}   // ✅ Forward Formik's onBlur

                    value={value}
                    disabled={disabled}
                    {...props}
                />
                <div className='absolute inset-y-0 right-4 flex items-center ps-3.5 z-50'>
                    {isError ? (
                        <AlertCircleIcon />
                    ) : Boolean(rightIcon) ? (
                        rightIcon
                    ) : null}
                </div>
            </div>
        ),
        website: (
            <div className='flex relative mb-1'>
                <span className='inline-flex items-center px-3 text-base text-gray200 border rounded-e-0 border-gray border-e-0 rounded-s-[8px]'>
                    http://
                </span>
                <input
                    className={clsxMerge(
                        inputStyles({
                            variant: disabled
                                ? 'leftNotchDisabled'
                                : isError
                                ? 'leftNotchError'
                                : 'leftNotch'
                        }),

                        className
                    )}
                    onChange={(e) => {
                        setValue(e.target.value);
                        onChange?.(e);
                    }}
                    onBlur={props.onBlur}   // ✅ Forward Formik's onBlur
                    value={value}
                    disabled={disabled}
                    {...props}
                />
                <div className='absolute inset-y-0 right-4 flex items-center ps-3.5 '>
                    {isError ? (
                        <AlertCircleIcon />
                    ) : Boolean(rightIcon) ? (
                        rightIcon
                    ) : (
                        <HelpCircleIcon />
                    )}
                </div>
            </div>
        ),

        copy: (
            <div className='flex mb-1'>
                <div className='relative w-full'>
                    <input
                        className={clsxMerge(
                            inputStyles({
                                variant: disabled
                                    ? 'rightNotchDisabled'
                                    : isError
                                    ? 'rightNotchError'
                                    : 'rightNotch'
                            }),
                            className
                        )}
                        disabled={disabled}
                        value={value}
                        onBlur={props.onBlur}   // ✅ Forward Formik's onBlur

                        onChange={(e) => {
                            setValue(e.target.value);
                            onChange?.(e);
                        }}
                        {...props}
                    />
                    <div className='absolute inset-y-0 right-4 flex items-center ps-3.5'>
                        {isError ? (
                            <AlertCircleIcon />
                        ) : Boolean(rightIcon) ? (
                            rightIcon
                        ) : (
                            <HelpCircleIcon />
                        )}
                    </div>
                </div>

                <CopyToClipboard
                    text={String(value)}
                    onCopy={() => toast('Value copied to clipboard!')}
                >
                    <button className='inline-flex items-center px-3 text-base text-gray700 border rounded-l-0 border-gray border-l-0 rounded-r-[8px] font-semibold gap-2'>
                        <CopyOutlineIcon /> Copy
                    </button>
                </CopyToClipboard>
            </div>
        ),

        tags: (
            <div
                className={`relative mb-1 flex flex-wrap items-center gap-2 p-4  bg-white  ${clsxMerge(
                    inputStyles({
                        variant: disabled
                            ? 'disabled'
                            : isError
                            ? 'error'
                            : 'default',
                        spacing: leftIcon ? 'left' : 'right'
                    }),
                    className
                )}`}
                onClick={() => inputRef.current?.focus()}
            >
                {tagValues.map((tag, index) => (
                    <div
                        key={`${index}-${tag}`}
                        className='border border-[#D0D5DD] px-2 py-1 rounded-md text-sm shadow-sm font-medium text-gray-700 flex items-center'
                    >
                        <p className='max-w-[100px] truncate'>{tag}</p>
                        <button
                            className='ml-2 text-gray-500 hover:text-red-500'
                            onClick={() => handleRemoveTag(tag)}
                            title={`Remove ${tag}`}
                        >
                            &times;
                        </button>
                    </div>
                ))}

                <input
                    className='focus:outline-none bg-transparent text-sm'
                    style={{
                        width: `${
                            String(value)?.length
                                ? String(value).length * 9 + 10
                                : 50
                        }px`,
                        minWidth: '50px'
                    }}
                    ref={inputRef}
                    onKeyDown={handleKeyPress}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={props.onBlur}   // ✅ Forward Formik's onBlur

                    value={value}
                    {...props}
                />
            </div>
        ),
        dropdownLeft: (
            <div className='flex relative mb-1'>
                <button
                    id='dropdown-button'
                    className='flex gap-4 items-center inline-flex items-center px-3 text-base text-gray200 border rounded-e-0 border-gray border-e-0 rounded-s-[8px]'
                    onClick={() => setOpenDropdown(!openDropdown)}
                >
                    {
                        dropdownOptions?.find(
                            (item) => item.value === dropdownValue
                        )?.label
                    }
                    <ChevronDownIcon />
                </button>
                {openDropdown && (
                    <div className='z-10 absolute top-[44px] bg-white divide-y divide-gray-100 rounded-lg shadow w-52 dark:bg-gray-700'>
                        <ul
                            className='py-2 text-sm text-gray-700 dark:text-gray-200'
                            aria-labelledby='dropdown-button'
                        >
                            {dropdownOptions?.map((item) => (
                                <li>
                                    <button
                                        type='button'
                                        className='inline-flex w-full px-4 py-2 text-sm text-gray100 hover:bg-gray'
                                        onClick={() => {
                                            setOpenDropdown(false);
                                            setDropdownValue?.(item.value);
                                        }}
                                    >
                                        <span className='inline-flex items-center'>
                                            {item.label}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <input
                    className={clsxMerge(
                        inputStyles({
                            variant: disabled
                                ? 'leftNotchDisabled'
                                : isError
                                ? 'leftNotchError'
                                : 'leftNotch'
                        }),
                        className
                    )}
                    disabled={disabled}
                    onChange={(e) => {
                        setValue(e.target.value);
                        onChange?.(e);
                    }}
                    onBlur={props.onBlur}   // ✅ Forward Formik's onBlur

                    value={value}
                    {...props}
                />
                <div className='absolute inset-y-0 right-4 flex items-center ps-3.5 '>
                    {isError ? (
                        <AlertCircleIcon />
                    ) : Boolean(rightIcon) ? (
                        rightIcon
                    ) : (
                        <HelpCircleIcon />
                    )}
                </div>
            </div>
        ),
        dropdownRight: (
            <div className='flex mb-1'>
                <div className='relative w-full'>
                    {openDropdown && (
                        <div className='z-10 absolute top-[44px] right-[-80px] bg-white divide-y divide-gray-100 rounded-lg shadow w-52'>
                            <ul
                                className='py-2 text-sm text-gray-700 dark:text-gray-200'
                                aria-labelledby='dropdown-button'
                            >
                                {dropdownOptions?.map((item) => (
                                    <li>
                                        <button
                                            type='button'
                                            className='inline-flex w-full px-4 py-2 text-sm text-gray100 hover:bg-gray'
                                            onClick={() => {
                                                setOpenDropdown(false);
                                                setDropdownValue?.(item.value);
                                            }}
                                        >
                                            <span className='inline-flex items-center'>
                                                {item.label}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <input
                        className={clsxMerge(
                            inputStyles({
                                variant: disabled
                                    ? 'rightNotchDisabled'
                                    : isError
                                    ? 'rightNotchError'
                                    : 'rightNotch'
                            }),
                            className
                        )}
                        disabled={disabled}
                        onChange={(e) => {
                            setValue(e.target.value);
                            onChange?.(e);
                        }}
                        onBlur={props.onBlur}   // ✅ Forward Formik's onBlur

                        value={value}
                        {...props}
                    />
                    <div className='absolute inset-y-0 right-4 flex items-center ps-3.5 '>
                        {isError ? (
                            <AlertCircleIcon />
                        ) : Boolean(rightIcon) ? (
                            rightIcon
                        ) : (
                            <HelpCircleIcon />
                        )}
                    </div>
                </div>

                <button
                    id='dropdown-button'
                    className='inline-flex items-center px-3 text-sm text-gray700 border rounded-l-0 border-gray border-l-0 rounded-r-[8px] gap-2'
                    onClick={() => setOpenDropdown(!openDropdown)}
                >
                    {
                        dropdownOptions?.find(
                            (item) => item.value === dropdownValue
                        )?.label
                    }
                    <ChevronDownIcon />
                </button>
            </div>
        )
    };
    return (
        <div className='' ref={ref}>
            <label
                htmlFor='input-group-1'
                className='block mb-2 text-sm font-medium text-gray700 text-left'
            >
                {label}
            </label>

            {component[variant]}

            {Boolean(errorMessage) ? (
                <p className='text-xs text-red100'>{errorMessage}</p>
            ) : Boolean(infoText) ? (
                <p className='text-xs text-gray200'>{infoText}</p>
            ) : null}
        </div>
    );
};

export default Input;
